import API_BASE from "./apiConfig";
import React, { useState, useEffect } from "react";
import {
  FaShieldAlt, FaSave, FaCheckCircle, FaExclamationCircle,
  FaLock, FaUnlock, FaEye, FaEdit,
  FaUser, FaRunning, FaLayerGroup, FaFileAlt, FaCog,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT } from "./ThemeContext";
const BASE = API_BASE;
const STATIC = {
  blur:"blur(18px)", cardRadius:"18px", inputRadius:"10px",
  yellow:Colors.logoYellow, blue:Colors.brandBlue||"#034e7b",
  blueDark:Colors.primaryBlue||"#023a5c", iconGray:Colors.iconGray||"#8899aa",
  font:"'Segoe UI', system-ui, -apple-system, sans-serif",
};
const yellowBtn=(T)=>({padding:"11px 22px",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",color:STATIC.blue,display:"flex",alignItems:"center",justifyContent:"center",gap:8,backgroundImage:`linear-gradient(135deg,${STATIC.yellow},#fff3a0)`,boxShadow:"0 4px 14px rgba(255,200,0,0.35)",fontFamily:STATIC.font});
const toastSt=(ok)=>({display:"flex",alignItems:"center",gap:10,padding:"13px 18px",borderRadius:10,marginBottom:20,fontSize:14,fontWeight:600,fontFamily:STATIC.font,background:ok?"rgba(212,237,218,0.92)":"rgba(248,215,218,0.92)",color:ok?"#155724":"#721c24",border:ok?"1px solid #b8dfc4":"1px solid #f3b8bc",backdropFilter:STATIC.blur, WebkitBackdropFilter:STATIC.blur,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"});

// ── Complete field definitions for every module in the application ──────────
const FIELD_GROUPS = [
  {
    module: "Student Master",
    icon: FaUser,
    color: "#3b82f6",
    fields: [
      { key:"first_name",      label:"First Name" },
      { key:"last_name",       label:"Last Name" },
      { key:"date_of_birth",   label:"Date of Birth" },
      { key:"gender",          label:"Gender" },
      { key:"join_date",       label:"Join Date" },
      { key:"active_status",   label:"Active Status" },
      { key:"skill_level_id",  label:"Skill Level" },
      { key:"student_code",    label:"Student Code" },
      { key:"parent_name",     label:"Parent Name" },
      { key:"parent_email",    label:"Parent Email" },
      { key:"parent_phone",    label:"Parent Phone" },
      { key:"photo_url",       label:"Photo / Photo URL" },
      { key:"current_program_id", label:"Current Program" },
      { key:"current_batch_id",   label:"Current Batch" },
    ],
  },
  {
    module: "Performance Update",
    icon: FaRunning,
    color: "#8b5cf6",
    fields: [
      { key:"performance_date",  label:"Performance Date" },
      { key:"indicator_scores",  label:"Indicator Scores (all categories)" },
      { key:"batting_scores",    label:"Batting Scores" },
      { key:"bowling_scores",    label:"Bowling Scores" },
      { key:"fielding_scores",   label:"Fielding Scores" },
      { key:"fitness_scores",    label:"Fitness Scores" },
      { key:"coach_comments",    label:"Coach Comments" },
      { key:"performance_total", label:"Total Score" },
    ],
  },
  {
    module: "Coach Attendance",
    icon: FaRunning,
    color: "#06b6d4",
    fields: [
      { key:"session_date",     label:"Session Date" },
      { key:"attendance_status",label:"Attendance Status (Present / Absent / Late)" },
      { key:"remarks",          label:"Student Remarks" },
      { key:"session_notes",    label:"Session Notes" },
      { key:"incident_report",  label:"Incident Report" },
      { key:"batch_id",         label:"Batch" },
      { key:"coach_id",         label:"Coach" },
    ],
  },
  {
    module: "Batch Management",
    icon: FaLayerGroup,
    color: "#10b981",
    fields: [
      { key:"batch_name",     label:"Batch Name" },
      { key:"level_id",       label:"Skill Level" },
      { key:"program_id",     label:"Program" },
      { key:"schedule_days",  label:"Schedule Days" },
      { key:"weekday_start",  label:"Weekday Start Time" },
      { key:"weekday_end",    label:"Weekday End Time" },
      { key:"saturday_start", label:"Saturday Start Time" },
      { key:"saturday_end",   label:"Saturday End Time" },
      { key:"max_capacity",   label:"Max Capacity" },
      { key:"description",    label:"Batch Description" },
      { key:"enroll_student", label:"Enroll Student" },
      { key:"unenroll_student",label:"Unenroll Student" },
    ],
  },
  {
    module: "Kiosk Check-In",
    icon: FaLayerGroup,
    color: "#f59e0b",
    fields: [
      { key:"kiosk_batch_id",    label:"Batch Selection" },
      { key:"kiosk_checkin",     label:"Check-In Action" },
      { key:"kiosk_student_view",label:"Student List" },
    ],
  },
  {
    module: "Reports",
    icon: FaFileAlt,
    color: "#ef4444",
    fields: [
      { key:"promotion_readiness",    label:"Promotion Readiness Report" },
      { key:"category_performance",   label:"Category Performance Report" },
      { key:"performance_report",     label:"Performance Report" },
      { key:"student_roster",         label:"Student Roster" },
      { key:"attendance_report",      label:"Attendance Report" },
      { key:"progress_summary",       label:"Progress Summary" },
      { key:"performance_change_log", label:"Performance Change Log" },
      { key:"export_data",            label:"Export / Download Reports" },
      { key:"filter_by_batch",        label:"Filter by Batch" },
      { key:"filter_by_level",        label:"Filter by Level" },
    ],
  },
  {
    module: "Config Master",
    icon: FaCog,
    color: "#64748b",
    fields: [
      // Organisation
      { key:"org_name",           label:"Organisation Name" },
      { key:"org_tagline",        label:"Tagline / Motto" },
      { key:"org_email",          label:"Org Email" },
      { key:"org_phone",          label:"Org Phone" },
      { key:"org_address",        label:"Org Address" },
      // Branding
      { key:"primary_color",      label:"Primary Colour" },
      { key:"secondary_color",    label:"Secondary Colour" },
      { key:"accent_color",       label:"Accent Colour" },
      { key:"font_family",        label:"Font Family" },
      { key:"sidebar_opacity",    label:"Sidebar Opacity" },
      { key:"logo",               label:"Logo Upload" },
      // Reports tab
      { key:"report_header",      label:"Report Header Text" },
      { key:"report_footer",      label:"Report Footer Text" },
      { key:"report_color_scheme",label:"Report Colour Scheme" },
      { key:"items_per_page",     label:"Items Per Page" },
      { key:"report_show_logo",   label:"Show Logo on Reports" },
      // System tab
      { key:"date_format",        label:"Date Format" },
      { key:"time_format",        label:"Time Format" },
      { key:"timezone",           label:"Timezone" },
      { key:"currency",           label:"Currency" },
      { key:"batch_start_hour",   label:"Batch Start Hour" },
      { key:"batch_end_hour",     label:"Batch End Hour" },
      { key:"max_students_per_batch", label:"Max Students per Batch" },
      { key:"enrollment_mode",    label:"Enrolment Mode" },
      { key:"session_timeout",    label:"Session Timeout" },
      { key:"password_min_length",label:"Password Min Length" },
      { key:"maintenance_mode",   label:"Maintenance Mode" },
      { key:"email_notifications_enabled", label:"Email Notifications" },
      { key:"admin_notification_email",    label:"Admin Notification Email" },
      // Lookup values tab
      { key:"lookup_student_statuses",    label:"Lookup — Student Statuses" },
      { key:"lookup_skill_levels",        label:"Lookup — Skill Levels" },
      { key:"lookup_programs",            label:"Lookup — Programs" },
      { key:"lookup_perf_categories",     label:"Lookup — Performance Categories" },
      { key:"lookup_perf_indicators",     label:"Lookup — Performance Indicators" },
    ],
  },
  {
    module: "User Master",
    icon: FaShieldAlt,
    color: "#dc2626",
    fields: [
      { key:"full_name",     label:"Full Name" },
      { key:"email",         label:"Email Address" },
      { key:"phone",         label:"Phone Number" },
      { key:"password_hash", label:"Password" },
      { key:"user_role",     label:"Role Assignment" },
      { key:"active_status", label:"Active Status" },
      { key:"parent_link",   label:"Parent Account Links" },
    ],
  },
  {
    module: "Security",
    icon: FaShieldAlt,
    color: "#b91c1c",
    fields: [
      { key:"role_name",         label:"Role Name" },
      { key:"role_description",  label:"Role Description" },
      { key:"role_permissions",  label:"Role Permissions" },
      { key:"field_auth_matrix", label:"Field Auth Matrix" },
    ],
  },
  {
    module: "Coach Evaluation",
    icon: FaFileAlt,
    color: "#f59e0b",
    fields: [
      { key:"submit_evaluation",  label:"Submit Coach Evaluation" },
      { key:"view_evaluation",    label:"View Evaluation Reports" },
    ],
  },
  {
    module: "Executive Reports",
    icon: FaShieldAlt,
    color: "#1e3a8a",
    fields: [
      { key:"perf_intel",    label:"Performance Intelligence" },
      { key:"finance_dash",   label:"Finance Dashboard" },
    ],
  },
];

// Access levels
const ACCESS = [
  { value:"none",  label:"None",       icon:FaLock,  color:"#e74c3c", bg:"rgba(220,0,0,0.08)",    border:"rgba(220,0,0,0.18)" },
  { value:"read",  label:"Read Only",  icon:FaEye,   color:"#2980b9", bg:"rgba(41,128,185,0.08)", border:"rgba(41,128,185,0.22)" },
  { value:"write", label:"Read/Write", icon:FaEdit,  color:"#27ae60", bg:"rgba(39,174,96,0.10)",  border:"rgba(39,174,96,0.25)" },
];

function AccessPill({value, onChange}) {
  const { isDark } = useTheme();
  const cur = ACCESS.find(a=>a.value===value) || ACCESS[0];
  return (
    <select value={value} onChange={e=>onChange(e.target.value)} style={{
      padding:"4px 8px", borderRadius:20, fontSize:11, fontWeight:700,
      background:cur.bg, border:`1px solid ${cur.border}`, color:cur.color,
      cursor:"pointer", outline:"none", fontFamily:STATIC.font, appearance:"auto",
      colorScheme: isDark ? "dark" : "light" }}>
      {ACCESS.map(a=><option key={a.value} value={a.value}>{a.label}</option>)}
    </select>
  );
}

// Sensible defaults per role/module
function defaultAccess(module, fieldKey, role) {
  if (role === "Admin") return "write";
  if (module === "User Master") return "none";
  if (module === "Security") return "none";
  if (module === "Config Master" && role !== "Admin") return "none";
  if (fieldKey === "password_hash") return "none";
  if (fieldKey === "field_auth_matrix") return "none";
  if (fieldKey === "enroll_student" || fieldKey === "unenroll_student") {
    return role === "Coach" ? "write" : "none";
  }
  if (module === "Reports") return role === "Staff" ? "none" : "read";
  if (module === "Kiosk Check-In") return "write";
  return "read";
}

export default function FieldAuthorization({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [roles,        setRoles]        = useState([]);
  const [matrix,       setMatrix]       = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [msg,          setMsg]          = useState(null);
  const [saving,       setSaving]       = useState(false);

  const buildMatrix = (roleList, saved = {}) => {
    const m = {};
    FIELD_GROUPS.forEach(grp => {
      m[grp.module] = {};
      grp.fields.forEach(f => {
        m[grp.module][f.key] = {};
        roleList.forEach(role => {
          const sv = saved?.[grp.module]?.[f.key]?.[role];
          m[grp.module][f.key][role] = sv !== undefined ? sv : defaultAccess(grp.module, f.key, role);
        });
      });
    });
    return m;
  };

  const fetchAll = async () => {
    let roleList = [];
    try {
      const r = await fetch(`${BASE}/roles/`);
      const d = await r.json();
      if (d.roles?.length) roleList = d.roles.map(r => r.role_name);
    } catch {}
    if (!roleList.length) roleList = ["Admin", "Coach", "Staff"];

    let saved = {};
    try {
      const r2 = await fetch(`${BASE}/field-auth/`);
      const d2 = await r2.json();
      if (d2.matrix) saved = d2.matrix;
    } catch {}

    setRoles(roleList);
    setSelectedRole(prev => roleList.includes(prev) ? prev : roleList[0]);
    setMatrix(buildMatrix(roleList, saved));
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    window.addEventListener("rolesUpdated", fetchAll);
    return () => window.removeEventListener("rolesUpdated", fetchAll);
  }, []);

  const setAccess = (module, field, role, val) => {
    setMatrix(prev => ({
      ...prev,
      [module]: { ...prev[module], [field]: { ...(prev[module]?.[field] || {}), [role]: val } },
    }));
  };

  const setAllInModule = (module, role, val) => {
    setMatrix(prev => {
      const grp = FIELD_GROUPS.find(g => g.module === module);
      const updated = { ...prev[module] };
      grp.fields.forEach(f => { updated[f.key] = { ...(updated[f.key] || {}), [role]: val }; });
      return { ...prev, [module]: updated };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/field-auth/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ matrix, updated_by: String(currentUserId || "system") }),
      });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message || (d.success ? "Saved!" : "Error saving") });
    } catch { setMsg({ ok: false, text: "Network error" }); }
    finally { setSaving(false); }
  };

  const thSt = { padding:"10px 14px", textAlign:"left", fontWeight:800, color:T.textPrimary, fontSize:11, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:STATIC.font, borderBottom:`1.5px solid ${T.divider}` };
  const tdSt = { padding:"8px 14px", borderBottom:"1px solid rgba(0,53,128,0.06)", fontSize:13, fontFamily:STATIC.font };

  return (
    <div style={{minHeight:"100%",padding:"20px 28px 48px",boxSizing:"border-box",fontFamily:STATIC.font}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:42,height:42,borderRadius:12,flexShrink:0,background:`linear-gradient(135deg,${STATIC.yellow},#fff3a0)`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 14px rgba(255,200,0,0.32)"}}>
            <FaShieldAlt size={18} color={STATIC.blue}/>
          </div>
          <div>
            <h2 style={{fontSize:22,fontWeight:800,color:T.textPrimary,margin:0,letterSpacing:"-0.01em"}}>Fields Authorization</h2>
            <p style={{margin:0,fontSize:12,color:T.textMuted,marginTop:1}}>Control read/write access per field per role — covers all {FIELD_GROUPS.reduce((a,g)=>a+g.fields.length,0)} fields across {FIELD_GROUPS.length} modules</p>
          </div>
        </div>
        <button onClick={save} disabled={saving} style={{...yellowBtn(T),opacity:saving?0.75:1}}>
          <FaSave size={14}/> {saving?"Saving...":"Save All"}
        </button>
      </div>

      {msg&&<div style={{...toastSt(msg.ok),marginBottom:16}}>{msg.ok?<FaCheckCircle/>:<FaExclamationCircle/>} {msg.text}</div>}

      {/* Role filter tabs */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {roles.map(r=>{
          const active = selectedRole===r;
          return(
            <button key={r} onClick={()=>setSelectedRole(r)} style={{
              padding:"9px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",
              display:"flex",alignItems:"center",gap:7,fontFamily:STATIC.font,
              color:active?STATIC.blue:"#5a6a80",
              background:active?`linear-gradient(135deg,${STATIC.yellow},#fff3a0)`:T.glass,
              backdropFilter:STATIC.blur,WebkitBackdropFilter:STATIC.blur,
              border:active?"1px solid rgba(255,200,0,0.4)":T.glassBdr,
              boxShadow:active?"0 4px 14px rgba(255,200,0,0.30)":T.glassSh,
            }}>
              <FaShieldAlt size={12}/> {r}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}>
        {ACCESS.map(a=>(
          <div key={a.value} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 12px",borderRadius:20,background:a.bg,border:`1px solid ${a.border}`}}>
            <a.icon size={11} style={{color:a.color}}/>
            <span style={{fontSize:12,fontWeight:700,color:a.color,fontFamily:STATIC.font}}>{a.label}</span>
          </div>
        ))}
        <div style={{marginLeft:"auto",fontSize:11,color:T.textMuted,alignSelf:"center",fontFamily:STATIC.font}}>
          Viewing permissions for: <strong style={{color:T.textPrimary}}>{selectedRole}</strong>
        </div>
      </div>

      {/* Tables per module */}
      {FIELD_GROUPS.map(grp => {
        const Ic = grp.icon;
        return (
          <div key={grp.module} style={{marginBottom:24}}>
            {/* Module header bar */}
            <div style={{
              display:"flex",alignItems:"center",gap:14,
              padding:"12px 20px",borderRadius:14,marginBottom:10,
              background:`linear-gradient(110deg,${STATIC.blueDark} 0%,rgba(3,62,98,0.95) 55%,rgba(2,50,80,0.98) 100%)`,
              backdropFilter:STATIC.blur,WebkitBackdropFilter:STATIC.blur,
              border:"1px solid rgba(255,255,255,0.16)",
              boxShadow:"0 6px 20px rgba(3,78,123,0.28),inset 0 1px 0 rgba(255,255,255,0.14)",
              position:"relative",overflow:"hidden",
            }}>
              <div style={{position:"absolute",left:0,top:0,bottom:0,width:4,background:`linear-gradient(180deg,${grp.color},${grp.color}99)`,borderRadius:"14px 0 0 14px"}}/>
              <div style={{width:32,height:32,borderRadius:8,background:`${grp.color}44`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 3px 10px ${grp.color}55`,marginLeft:6}}>
                <Ic size={14} color="rgba(255,255,255,0.92)"/>
              </div>
              <span style={{fontWeight:800,color:"rgba(255,255,255,0.92)",fontSize:15,flex:1,fontFamily:STATIC.font,letterSpacing:"0.02em",textShadow:"0 1px 3px rgba(0,0,0,0.20)"}}>
                {grp.module}
              </span>
              <span style={{padding:"3px 10px",borderRadius:20,background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.22)",fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.80)",fontFamily:STATIC.font}}>
                {grp.fields.length} fields
              </span>
              {/* Quick-set buttons */}
              <div style={{display:"flex",gap:6}}>
                {ACCESS.map(a=>(
                  <button key={a.value} onClick={()=>setAllInModule(grp.module,selectedRole,a.value)} style={{
                    padding:"4px 10px",borderRadius:6,border:"1px solid rgba(255,255,255,0.22)",
                    background:"rgba(255,255,255,0.10)",cursor:"pointer",fontSize:11,fontWeight:700,
                    color:"rgba(255,255,255,0.85)",fontFamily:STATIC.font,
                  }}>
                    All {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{background:T.glass,backdropFilter:STATIC.blur,WebkitBackdropFilter:STATIC.blur,border:T.glassBdr,borderRadius:14,boxShadow:T.glassSh,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:"45%"}}/><col style={{width:"55%"}}/>
                </colgroup>
                <thead>
                  <tr style={{background:"rgba(0,53,128,0.07)"}}>
                    <th style={thSt}>Field</th>
                    <th style={thSt}>Access for <span style={{color:STATIC.yellow}}>{selectedRole}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {grp.fields.map((f, idx) => {
                    const val = matrix[grp.module]?.[f.key]?.[selectedRole] || "none";
                    const cur = ACCESS.find(a => a.value === val) || ACCESS[0];
                    return (
                      <tr key={f.key}
                        style={{background:idx%2===0?T.rowEven:T.rowOdd}}
                        onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,200,0,0.08)")}
                        onMouseLeave={e=>(e.currentTarget.style.background=idx%2===0?T.rowEven:T.rowOdd)}>
                        <td style={{...tdSt,fontWeight:600,color:T.textPrimary}}>{f.label}</td>
                        <td style={tdSt}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <AccessPill value={val} onChange={v=>setAccess(grp.module,f.key,selectedRole,v)}/>
                            <cur.icon size={11} style={{color:cur.color,flexShrink:0}}/>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
